import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../Toast';
import { EmptyState } from '../EmptyState';
import { formatCents } from '../../lib/billing-admin-queries';
import {
  chargeAuctionWinner,
  refundAuctionCharge,
  fetchPostagePreview,
  buyAuctionLabel,
  saleroomChargeGroup,
  type SaleroomChargeRow,
  type SaleroomChargeGroup,
} from '../../lib/saleroom-admin-queries';

/**
 * Admin "confirm winner & charge" dashboard for the Saleroom. Lists every
 * ended lot with a winner, the ledger status, and the right action:
 *   • no charge yet  → "Charge winner" (off-session PaymentIntent)
 *   • failed/canceled → "Retry charge"
 *   • requires_action → member must complete SCA (no admin action)
 *   • succeeded       → "Refund"
 * All money movement happens server-side; this only triggers it.
 *
 * Rows are owned by Admin (so the nav-tab indicator can count outstanding work
 * without opening the tab); this component renders them, filters by status, and
 * calls onRefresh after any charge/refund.
 */

const STATUS_LABEL: Record<string, string> = {
  awaiting: 'Awaiting charge',
  pending_review: 'Awaiting charge',
  pending: 'Charging…',
  processing: 'Processing',
  requires_action: 'Needs member SCA',
  succeeded: 'Paid',
  failed: 'Failed',
  canceled: 'Canceled',
  refunded: 'Refunded',
};

// Status sub-tabs. 'action' leads - it's the work the team still has to do.
const SALEROOM_TABS: Array<{ id: SaleroomChargeGroup | 'all'; label: string }> = [
  { id: 'action', label: 'Action needed' },
  { id: 'progress', label: 'In progress' },
  { id: 'paid', label: 'Paid' },
  { id: 'refunded', label: 'Refunded' },
  { id: 'all', label: 'All' },
];

// Maps a ledger status to the pill colour classes already in Admin.css.
function pillClass(status: string): string {
  switch (status) {
    case 'succeeded': return 'pill pill--active';
    case 'failed':
    case 'canceled': return 'pill pill--invited';
    case 'refunded': return 'pill pill--role';
    default: return 'pill pill--badge';
  }
}

export function SaleroomCharges({
  rows,
  loading,
  loaded,
  error,
  onRefresh,
}: {
  rows: SaleroomChargeRow[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  onRefresh: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [busyPost, setBusyPost] = useState<string | null>(null);
  const [tab, setTab] = useState<SaleroomChargeGroup | 'all'>('action');

  async function handleCharge(row: SaleroomChargeRow) {
    setBusyPost(row.post_id);
    // Exact breakdown first (067): live carrier rate or flat postage - the
    // admin confirms the real total, never a stale flat figure.
    let confirmText: string;
    try {
      const p = await fetchPostagePreview(row.post_id);
      const postageLine =
        p.postage_source === 'live_rate'
          ? `+ ${formatCents(p.postage_cents)} postage (live rate${p.postage_service ? ` - ${p.postage_service}` : ''})`
          : p.postage_source === 'flat'
          ? `+ ${formatCents(p.postage_cents)} flat postage`
          : '+ no postage set';
      confirmText =
        `Charge ${row.winner_name || 'the winner'} ${formatCents(p.total_cents)} for ` +
        `LOT ${row.lot_number} - ${row.lot_name}?\n\n` +
        `Hammer ${formatCents(p.hammer_cents)} ${postageLine}` +
        `${p.destination ? `\nShips to: ${p.destination}` : '\nNo delivery address on file.'}\n\n` +
        `This immediately charges their saved card off-session. It cannot be undone except by a refund.`;
    } catch (err: any) {
      setBusyPost(null);
      toast.error(err?.message ?? 'Could not fetch the postage quote - charge not started.');
      return;
    }
    const ok = window.confirm(confirmText);
    if (!ok) { setBusyPost(null); return; }
    try {
      const res = await chargeAuctionWinner(row.post_id);
      if (res.ok && res.status === 'succeeded') toast.success(`Charged: ${formatCents(row.amount_cents)} captured.`);
      else if (res.status === 'requires_action') toast.success('Charge started: the member must complete a security check.');
      else if (res.status === 'processing') toast.success('Charge processing: status will update shortly.');
      else toast.error(`Charge ${res.status}${res.error ? `: ${res.error}` : ''}.`);
      await onRefresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Charge failed.');
    } finally {
      setBusyPost(null);
    }
  }

  async function handleBuyLabel(row: SaleroomChargeRow) {
    setBusyPost(row.post_id);
    try {
      const res = await buyAuctionLabel(row.post_id);
      toast.success(res.tracking_number ? `Label bought - tracking ${res.tracking_number}.` : 'Label bought.');
      window.open(res.label_url, '_blank', 'noopener');
      await onRefresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Label purchase failed.');
    } finally {
      setBusyPost(null);
    }
  }

  async function handleRefund(row: SaleroomChargeRow) {
    const ok = window.confirm(
      `Refund ${formatCents(row.amount_cents)} to ${row.winner_name || 'the winner'} for ` +
      `LOT ${row.lot_number}? This returns the full amount.`,
    );
    if (!ok) return;
    setBusyPost(row.post_id);
    try {
      await refundAuctionCharge(row.post_id);
      toast.success('Refunded.');
      await onRefresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Refund failed.');
    } finally {
      setBusyPost(null);
    }
  }

  function renderAction(row: SaleroomChargeRow) {
    const busy = busyPost === row.post_id;
    const status = row.charge_status ?? 'awaiting';
    if (status === 'awaiting' || status === 'pending_review' || status === 'failed' || status === 'canceled') {
      if (!row.winner_has_card) {
        return <span className="admin__note" style={{ margin: 0 }} title="Winner has no card on file">No card</span>;
      }
      return (
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => handleCharge(row)}
          disabled={busy}
        >
          {busy ? '…' : status === 'failed' || status === 'canceled' ? 'Retry charge' : 'Charge winner'}
        </button>
      );
    }
    if (status === 'succeeded') {
      return (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          {row.label_url ? (
            <a className="btn btn--ghost btn--sm" href={row.label_url} target="_blank" rel="noopener noreferrer">
              Label
            </a>
          ) : (
            <button type="button" className="btn btn--primary btn--sm" onClick={() => handleBuyLabel(row)} disabled={busy}>
              {busy ? '…' : 'Buy label'}
            </button>
          )}
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => handleRefund(row)} disabled={busy}>
            {busy ? '…' : 'Refund'}
          </button>
        </span>
      );
    }
    // pending / processing / requires_action / refunded - no admin action.
    return <span className="admin__note" style={{ margin: 0 }}> - </span>;
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Saleroom</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  // Per-group counts for the sub-tab badges.
  const counts: Record<SaleroomChargeGroup, number> = { action: 0, progress: 0, paid: 0, refunded: 0 };
  for (const r of rows) counts[saleroomChargeGroup(r.charge_status)]++;

  const visibleRows = tab === 'all' ? rows : rows.filter((r) => saleroomChargeGroup(r.charge_status) === tab);

  return (
    <>
      <div className="admin__panel-head">
        <h2>Saleroom</h2>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onRefresh()} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      <p className="admin__note">
        Every ended lot with a winner. Charging captures the winner's saved card off-session for the
        hammer price plus postage - a live carrier rate to the winner's address when the lot has
        parcel details, otherwise the flat figure; the confirm dialog shows the exact breakdown
        before anything is charged. After payment, <strong>Buy label</strong> purchases the shipping
        label and tracking. A <strong>Needs member SCA</strong> charge
        is waiting on the member to finish a bank security check from their account.
      </p>

      {rows.length === 0 ? (
        <EmptyState
          tone="compact"
          align="start"
          eyebrow="Nothing to settle"
          headline="No ended auctions with a winner yet."
          body="Once a lot closes with at least one bid, it shows here to charge."
        />
      ) : (
        <>
          <div className="admin__tabs" role="tablist" aria-label="Saleroom status filter">
            {SALEROOM_TABS.map((t) => {
              const count = t.id === 'all' ? rows.length : counts[t.id];
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={`admin__tab ${tab === t.id ? 'admin__tab--active' : ''}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                  <span className="admin__tab-count">{count}</span>
                </button>
              );
            })}
          </div>

          {visibleRows.length === 0 ? (
            <EmptyState
              tone="compact"
              align="start"
              eyebrow="Clear"
              headline="Nothing in this view."
              body="No lots are currently in this status."
            />
          ) : (
            <div className="admin__table-wrap">
              <table className="admin__table">
                <thead>
                  <tr>
                    <th>Lot</th>
                    <th>Winner</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const status = row.charge_status ?? 'awaiting';
                    return (
                      <tr key={row.post_id}>
                        <td>
                          <strong>LOT {row.lot_number}</strong>
                          <span style={{ display: 'block', color: 'var(--text-soft)', fontSize: '0.85em' }}>
                            {row.post_slug
                              ? <Link to={`/p/${row.post_slug}`}>{row.lot_name}</Link>
                              : row.lot_name}
                          </span>
                        </td>
                        <td>
                          {row.winner_name || ' - '}
                          {row.winner_handle && (
                            <span style={{ display: 'block', color: 'var(--text-soft)', fontSize: '0.85em' }}>
                              @{row.winner_handle}
                            </span>
                          )}
                        </td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatCents(row.amount_cents)}
                          {/* Ledger postage (what was actually charged) wins over
                              the lot's flat figure once a charge exists. */}
                          {row.postage_cents != null && row.postage_cents > 0 ? (
                            <span style={{ display: 'block', color: 'var(--text-soft)', fontSize: '0.8em' }}>
                              incl. {formatCents(row.postage_cents)} postage
                              {row.postage_source === 'live_rate' && (row.postage_service ? ` - ${row.postage_service}` : ' - live rate')}
                            </span>
                          ) : row.postage_cents == null && row.shipping_cost != null && row.shipping_cost > 0 && (
                            <span style={{ display: 'block', color: 'var(--text-soft)', fontSize: '0.8em' }}>
                              incl. {formatCents(row.shipping_cost)} flat postage
                            </span>
                          )}
                          {row.tracking_number && (
                            <span style={{ display: 'block', color: 'var(--text-soft)', fontSize: '0.8em' }}>
                              {row.tracking_url
                                ? <a href={row.tracking_url} target="_blank" rel="noopener noreferrer">{row.tracking_number}</a>
                                : row.tracking_number}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={pillClass(status)}>{STATUS_LABEL[status] ?? status}</span>
                          {row.last_error && (status === 'failed' || status === 'canceled') && (
                            <span style={{ display: 'block', color: 'var(--text-soft)', fontSize: '0.8em', marginTop: 2 }}
                                  title={row.last_error}>
                              {row.last_error.slice(0, 48)}
                            </span>
                          )}
                        </td>
                        <td>{renderAction(row)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
